import * as Yup from 'yup';

import { Dayjs, dayjs } from '@ui';

import { ifscCodeErrorMsg } from '../utils/constants';

export const stringRequired = Yup.string().required('Required');
export const numberRequired = Yup.number()
  .transform((value, originalValue) => {
    // Convert empty string to undefined to prevent NaN
    return originalValue === '' ? undefined : value;
  })
  .required('Required');
export const booleanRequired = Yup.boolean().required('Required');
export const booleanSchemaDefault = Yup.boolean().default(false);

export const maxDate = dayjs()
  .subtract(18, 'years')
  .endOf('day')
  .format('DD/MM/YYYY');
export const maxDateIST = dayjs()
  .subtract(18, 'years')
  .endOf('day')
  .toLocaleString();
export const currentDateIST = dayjs().endOf('day').toLocaleString();

const minDate = dayjs().subtract(60, 'years').endOf('day').format('DD/MM/YYYY');
const minDateIST = dayjs().subtract(60, 'years').endOf('day').toLocaleString();

const noSpecialChars = /^[A-Za-z0-9 ]*$/;

export const stringWithoutSpecialChars = Yup.string().matches(
  noSpecialChars,
  'Special characters are not allowed'
);

const sentenceWithPunctuation = /^[A-Za-z0-9 , .]*$/;

export const sentenceWithPunctuationAllowed = Yup.string().matches(
  sentenceWithPunctuation,
  'Special characters are not allowed'
);

export const getLowestParentDob = (values: any) => {
  return values?.insured_basic?.reduce(
    (lowestAge: Dayjs | undefined, member: any) => {
      if (['Self', 'Spouse'].includes(member.relation)) {
        //age is a dayjs object here
        const age = member.dob ? dayjs(member.dob) : undefined;
        if (lowestAge === undefined || (age !== undefined && lowestAge < age)) {
          lowestAge = age;
        }
      }
      return lowestAge;
    },
    undefined
  );
};

export const sentenceWithSpace = /^[A-Za-z ]*$/;

export const dateNotRequired = Yup.date()
  .typeError('Invalid')
  .min(dayjs('01/01/1900', 'DD/MM/YYYY'), 'Invalid date')
  .max(currentDateIST, 'Future dates not allowed');

export const dateRequired = Yup.date()
  .required('Required')
  .typeError('Invalid')
  .min(dayjs('01/01/1900', 'DD/MM/YYYY'), 'Invalid date')
  .max(currentDateIST, 'Future dates not allowed');

export const fullNameValidation = Yup.string()
  .matches(/^[A-Za-z ]+$/, 'Only alphabets allowed.')
  .min(2, 'Too Short!')
  .max(60, 'Too Long!')
  .test('test-full-name', 'Invalid Full name', function (value) {
    const splits = (value || '').split(' ');
    const length = splits.length;
    if (splits[0] === '')
      return this.createError({
        path: this.path,
        message: 'Fullname Required'
      });
    if (splits[1] === '')
      return this.createError({
        path: this.path,
        message:
          length === 2
            ? 'Enter Middle and Last names'
            : 'Multiple spaces not allowed after First name'
      });
    if (splits[length - 2] === '')
      return this.createError({
        path: this.path,
        message: 'Multiple spaces not allowed before Last name'
      });
    if (splits[length - 1] === '')
      return this.createError({
        path: this.path,
        message: 'Space not allowed after Last name'
      });
    if (length < 2)
      return this.createError({
        path: this.path,
        message: 'Enter both First and Last names'
      });
    if (length > 2) {
      const names = [...splits];
      names.shift();
      names.pop();
      if (/\s\s/.test(names.join(' '))) {
        return this.createError({
          path: this.path,
          message: 'Multiple spaces in Middle name not allowed'
        });
      }
      if (names.join().length > 25)
        return this.createError({
          path: this.path,
          message: 'Middle name should not be more than 25 characters'
        });
    }
    if (
      (splits[0] && (splits[0].length > 25 || splits[0].length < 2)) ||
      (splits[length - 1] &&
        (splits[length - 1].length > 25 || splits[length - 1].length < 2))
    )
      return this.createError({
        path: this.path,
        message: 'First and Last names should contain 2–25 characters'
      });
    return true;
  });

export const fullNameRequired = fullNameValidation.required('Required');

export const dobRequired = Yup.date()
  .typeError('Invalid')
  .required('Required')
  .max(maxDateIST, `You should be 18 years old (must born before ${maxDate})`)
  .min(
    minDateIST,
    `Age should not be more than 60 years (must born after ${minDate})`
  );

export const genderRequired = Yup.string()
  .matches(/(Male|Female)/, 'Required')
  .required('Required');

export const indianResidentRequired = Yup.boolean()
  .required('Select Yes or No')
  .oneOf(
    [true],
    'We are currently supporting only Indian residents. Please get in touch with us if you are an NRI'
  );

export const panRequired = Yup.string()
  .test('valid-pan', 'Enter a valid PAN', (value): boolean => {
    return /^[A-Z]{3}[P]{1}[A-Z]{1}[0-9]{4}[A-Z]{1}$/.test(
      (value && value.toString().toUpperCase()) || ''
    );
  })
  .required('Required');

const mobileRegex = /^[6-9]{1}[0-9]{9}$/;
export const mobileRequired = Yup.string()
  .test('valid-mobile', 'Enter a valid mobile number', (value): boolean => {
    return mobileRegex.test((value && value.toString()) || '');
  })
  .required('Enter a valid mobile number');

//Mobile number validation (Non-mandatory field)
export const mobileNotRequired = Yup.string().test(
  'valid-mobile',
  'Enter a valid mobile number',
  (value): boolean => {
    // If value is empty or undefined, return true (field is not mandatory)
    if (!value) return true;

    // Otherwise check if it matches the pattern
    return mobileRegex.test(value.toString());
  }
);

/**
 * Basic email validation that checks for required fields and general email format.
 * Example: Use this for cases like HDFC ERGO, where specific characters (e.g., '+') are not allowed.
 * Additional custom validations can be added on top of this base validation.
 */
const emailPattern =
  /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|((([a-zA-Z\-0-9]{2,})+\.)+[a-zA-Z]{2,}))$/;
export const basicEmailRequired = Yup.string()
  .required('Required')
  .email('Enter a valid email')
  .test('valid-email', 'Enter a valid email', (value): boolean => {
    return emailPattern.test((value && value.toString()) || '');
  })
  .max(254, 'Max characters allowed 254');

//Email validation (Non-mandatory field)
export const basicEmailNotRequired = Yup.string()
  .email('Enter a valid email')
  .test('valid-email', 'Enter a valid email', (value): boolean => {
    // If value is empty or undefined, return true (field is not mandatory)
    if (!value) return true;

    return emailPattern.test(value.toString());
  })
  .max(254, 'Max characters allowed 254');

/**
 * Extended email validation with commonly allowed special characters.
 * This validation builds upon basicEmailRequired and adds an additional test for allowed special characters.
 * For custom requirements (e.g., HDFC ERGO), use basicEmailRequired and add specific validations as needed.
 */
const emailAllowedCharsRegex = /^[a-zA-Z0-9-+_.~@]+$/;
export const emailRequired = basicEmailRequired.test(
  'valid-email-2',
  'Allowed special chars are - + _ . ~ @',
  (value): boolean => {
    const email = (value && value.toString()) || '';
    return emailAllowedCharsRegex.test(email);
  }
);

//Email validation (Non-mandatory field)
export const emailNotRequired = basicEmailNotRequired.test(
  'valid-email-2',
  'Allowed special chars are - + _ . ~ @',
  (value): boolean => {
    if (!value) return true; // If value is empty or undefined, return true (field is not mandatory)
    const email = (value && value.toString()) || '';
    return emailAllowedCharsRegex.test(email);
  }
);

export const pinRequired = Yup.number()
  .test('valid-pin', 'Enter a valid pincode', (value): boolean => {
    return /^[1-9]{1}[0-9]{5}$/.test((value && value.toString()) || '');
  })
  .typeError('Required')
  .required('Required');

/**
 * Use this schema to validate answer and details.
 * Useful for schemas that represent a question that has an answer and details.
 *
 * Eg: Do you smoke cigarettes? Yes/No
 * If the answer is Yes, then the details will be the number of cigarettes smoked per day.
 * If the answer is No, then the details will be empty.
 *
 * Pass the schema for the details as the argument.
 */
export type AnswerDetails<T> =
  | {
      answer: false;
      details?: never;
    }
  | {
      answer: true;
      details: T;
    };

export const AnswerDetailsSchema = <T extends Yup.AnySchema>(
  schema: T
): Yup.ObjectSchema<AnswerDetails<Yup.InferType<T>>> =>
  Yup.object({
    answer: booleanRequired.default(false),
    details: schema.when('answer', {
      is: true,
      then: (schema) => schema,
      otherwise: (schema) => schema.optional().strip()
    })
  }) as any;

export const stringWithoutSpaces = Yup.string().test(
  'no-space-allowed',
  'Spaces are not allowed',
  (val: any) => {
    if (val) return /\s/.test(val) == false;
    return true;
  }
);

export const INDIAN_PASSPORT_REGEX = /^([A-Z][0-9]{7}|[A-Z]{2}[0-9]{6})$/;
export const INDIAN_VOTER_ID_REGEX = /^[A-Z]{3}[0-9]{7}$/;
export const INDIAN_DRIVING_LICENSE_REGEX = /^[A-Z]{2}[0-9]{13}$/;

export const ifscRegex = /^[A-Z]{4}[0][A-Z0-9]{6}$/;

export const ifscValidation = Yup.string()
  .matches(ifscRegex, ifscCodeErrorMsg)
  .required('Required');

export const accountNumberValidation = Yup.string()
  .min(2, 'Minimum 2 digits required')
  .max(20, 'Maximum of 20 digits allowed')
  .required('Required');

export const ifscValidationNotRequired = Yup.string().matches(
  /^[A-Z]{4}0[A-Z0-9]{6}$/,
  ifscCodeErrorMsg
);
